import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ConversionQualityIndicator from "./ConversionQualityIndicator";
import LegoInstructionGenerator from "./LegoInstructionGenerator";
import OptimizedBrickLayout from "./OptimizedBrickLayout";
import BOMGenerator from "./BOMGenerator";
import SectionRule from "./SectionRule";
import { Button } from "../@/components/ui/button";
import { LayerData } from "../utils/brickOptimizer";
import { DEPTH_BANDS } from "../utils/depthLayers";
import { exportBomData } from "../utils/bomExport";
import { exportLDraw } from "../utils/ldrExport";
import { exportMosaicPng } from "../utils/mosaicPngExport";

interface PixelData {
  pixels: number[][];
  width: number;
  height: number;
}

interface ModelInput {
  layers: LayerData[];
  width: number;
  height: number;
}

interface ConversionResultsProps {
  /** Image flow: the converted mosaic grid */
  pixelData?: PixelData;
  pixelSize?: number;
  depth3d?: boolean;
  /** 3D-model flow: pre-built brick layers from a mesh */
  model?: ModelInput;
}

const ConversionResults: React.FC<ConversionResultsProps> = ({
  pixelData,
  pixelSize,
  depth3d = false,
  model,
}) => {
  const { t } = useTranslation();
  const [generatedLayers, setGeneratedLayers] = useState<LayerData[]>([]);
  const [is3D, setIs3D] = useState(false);

  const isModel = !!model;
  const layers = model?.layers ?? generatedLayers;
  const firstLayer = layers[0];
  const gridWidth = model?.width ?? pixelData?.width ?? 0;
  const gridHeight = model?.height ?? pixelData?.height ?? 0;

  // Layers are generated by LegoInstructionGenerator's useLayerGeneration effect.
  // No stale-layer clearing needed: App unmounts this component when inputs reset.

  return (
    <>
      {/* In-page TOC — visible after the preview; hidden in print */}
      <nav
        aria-label={t("common.jumpTo")}
        className="print:hidden mb-10"
      >
        <div className="panel-inset px-4 sm:px-5 py-3 flex items-center gap-x-3 gap-y-2 flex-wrap text-sm">
          <span className="eyebrow text-brick">{t("common.jumpTo")}</span>
          {!isModel && pixelData && (
            <>
              <a
                href="#section-quality"
                className="font-semibold text-ink hover:text-brick underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick rounded-sm"
              >
                {t("quality.title")}
              </a>
              <span aria-hidden className="text-ink/30">
                ·
              </span>
            </>
          )}
          <a
            href="#section-instructions"
            className="font-semibold text-ink hover:text-brick underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick rounded-sm"
          >
            {t("instructions.title")}
          </a>
          {firstLayer && (
            <>
              <span aria-hidden className="text-ink/30">
                ·
              </span>
              <a
                href="#section-layout"
                className="font-semibold text-ink hover:text-brick underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick rounded-sm"
              >
                {t("instructions.legoLayout")}
              </a>
            </>
          )}
          {layers.length > 0 && (
            <>
              <span aria-hidden className="text-ink/30">
                ·
              </span>
              <a
                href="#section-bom"
                className="font-semibold text-ink hover:text-brick underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick rounded-sm"
              >
                {t("bom.title")}
              </a>
            </>
          )}
        </div>
      </nav>

      {/* Image flow — 01: Conversion quality */}
      {!isModel && pixelData && (
        <section aria-label={t("converter.title")}>
          <SectionRule number="01" id="section-quality" />
          <ConversionQualityIndicator pixelData={pixelData} />
        </section>
      )}

      {/* 02 (image) / 01 (model) — Building instructions */}
      <section className={isModel ? "" : "mt-14"}>
        <SectionRule
          number={isModel ? "01" : "02"}
          id="section-instructions"
        />
        <LegoInstructionGenerator
          pixelData={pixelData}
          onLayersGenerated={isModel ? undefined : setGeneratedLayers}
          depthBands={depth3d ? DEPTH_BANDS : 1}
          layersOverride={model?.layers}
        />
      </section>

      {/* 03 (image) / 02 (model) — Brick layout */}
      {firstLayer && (
        <section className="mt-14">
          <SectionRule
            number={isModel ? "02" : "03"}
            id="section-layout"
            toolbar={
              <div className="flex items-center gap-2">
                {isModel && (
                  <span className="spec-chip text-ink">
                    {gridWidth} × {gridHeight}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIs3D((prev) => !prev)}
                >
                  {is3D ? t("controls.hide3DView") : t("controls.show3DView")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="print:hidden"
                  onClick={() => exportLDraw(layers)}
                >
                  {t("controls.exportLDR")}
                </Button>
                {!isModel && pixelData && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="print:hidden"
                    onClick={() =>
                      exportMosaicPng(
                        pixelData.pixels,
                        pixelData.width,
                        pixelData.height,
                      )
                    }
                  >
                    {t("controls.exportPNG")}
                  </Button>
                )}
              </div>
            }
          />
          <h2 className="sr-only">{t("instructions.legoLayout")}</h2>
          <div className="panel p-4 sm:p-6">
            <OptimizedBrickLayout
              bricks={layers.flatMap((layer) => layer.bricks)}
              width={gridWidth}
              height={gridHeight}
              pixelSize={pixelSize ?? 20}
              is3D={is3D}
            />
          </div>
        </section>
      )}

      {/* 04 (image) / 03 (model) — Bill of materials */}
      {layers.length > 0 && (
        <section className="mt-14">
          <SectionRule number={isModel ? "03" : "04"} id="section-bom" />
          <BOMGenerator
            layers={layers}
            onExport={exportBomData}
          />
        </section>
      )}
    </>
  );
};

export default ConversionResults;
